import { Box, Tab, Tabs, Typography } from '@mui/material';
import { useIntl } from 'react-intl';
import { useClinicalListsListPeople } from '~/backend/connector-next-api/connector-next-components';
import { useConnectorIdentity } from '~/hooks/auth/useConnectorIdentity';
import type { CatalogMessageId } from '~/i18n';
import { segmentedTabsSx } from '~/theme/theme';
import { usePatientCounts } from './hooks';
import {
    NO_PATIENT_FILTERS,
    patientFilterQueryParams,
    withAssignedToUserId,
} from './model';

export type PatientListView = 'tasks' | 'all';

/** The views in tab order, each with its name. */
const VIEWS: readonly { view: PatientListView; labelId: CatalogMessageId }[] = [
    { view: 'tasks', labelId: 'patientList.view.tasks' },
    { view: 'all', labelId: 'patientList.view.all' },
];

/** A view's name with the number of patients it holds. */
function TabLabel({
    label,
    count,
}: {
    label: string;
    count: number | undefined;
}) {
    return (
        <Box
            component="span"
            sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.75 }}
        >
            {label}
            {count !== undefined && (
                <Typography variant="monoMeta">{count}</Typography>
            )}
        </Box>
    );
}

/**
 * The views as tabs with their counts. Where the connector identifies nobody,
 * nobody can hold a patient, so only All patients is offered and a sentence
 * says why.
 */
export default function PatientListTabs({
    view,
    onChange,
    focusSelected,
}: {
    view: PatientListView;
    onChange: (view: PatientListView) => void;
    /** Whether the selected tab takes focus as it mounts, after the user picked a view. */
    focusSelected: boolean;
}) {
    const intl = useIntl();
    const { signInEnabled } = useConnectorIdentity();
    const meId = useClinicalListsListPeople({}).data?.meId ?? undefined;
    const counts = usePatientCounts([
        [
            'tasks',
            meId === undefined
                ? undefined
                : patientFilterQueryParams(
                      withAssignedToUserId(NO_PATIENT_FILTERS, meId)
                  ),
        ],
        ['all', {}],
    ]);
    return (
        <>
            <Tabs
                value={view}
                onChange={(_, next: PatientListView) => onChange(next)}
                sx={segmentedTabsSx}
            >
                {VIEWS.filter(
                    (each) => signInEnabled || each.view === 'all'
                ).map((each) => (
                    <Tab
                        key={each.view}
                        value={each.view}
                        autoFocus={focusSelected && each.view === view}
                        label={
                            <TabLabel
                                label={intl.formatMessage({
                                    id: each.labelId,
                                })}
                                count={counts.get(each.view)}
                            />
                        }
                    />
                ))}
            </Tabs>
            {!signInEnabled && (
                <Typography variant="body2" color="text.secondary">
                    {intl.formatMessage({ id: 'patientList.view.needsSignIn' })}
                </Typography>
            )}
        </>
    );
}
